import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import api from '../api';
import { UserPreferences, DEFAULT_PREFERENCES } from '../types';
import { applyPreferencesPatch, PreferencesPatch } from '../patch';
import { Context as AuthContext } from '../../auth/context';
import Context from './context';
import { useDisplayZoom } from '../useDisplayZoom';
import { useSidebarWidth } from '../useSidebarWidth';

interface Props {
    children: React.ReactElement;
}

/** Tabs of this app in the same browser tell each other about saved changes here. */
const CHANNEL_NAME = 'taytan-user-preferences';

interface ChannelMessage {
    userId: string;
    patch: PreferencesPatch;
}

const UserPreferencesProvider = ({ children }: Props) => {
    const { currUser } = useContext(AuthContext);
    const userId = currUser?._id != null ? String(currUser._id) : null;

    const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const fetchPromiseRef = useRef<Promise<void> | null>(null);
    const loadedRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestPrefsRef = useRef<UserPreferences>(DEFAULT_PREFERENCES);
    // Starts empty so the first signed-in user triggers a load below.
    const userIdRef = useRef<string | null>(null);
    // Bumped whenever the preferences held here stop belonging to the signed-in
    // user. A load that started under an older generation is discarded.
    const generationRef = useRef(0);
    // Changes other tabs saved while this one was still loading. The load may
    // have read the row before those tabs' writes landed, so they are applied
    // on top of it.
    const remotePatchesRef = useRef<PreferencesPatch[]>([]);
    const channelRef = useRef<BroadcastChannel | null>(null);
    // Settings the user changed while their preferences could not be read.
    // Shown straight away, saved once the load succeeds — not discarded by it.
    const unsavedChoicesRef = useRef<Partial<UserPreferences>>({});
    const commitRef = useRef<((patch: PreferencesPatch, immediate: boolean) => boolean) | null>(null);

    const setLocal = useCallback((next: UserPreferences) => {
        latestPrefsRef.current = next;
        setPreferences(next);
    }, []);

    const fetch = useCallback(() => {
        if (loadedRef.current) return Promise.resolve();
        if (fetchPromiseRef.current) return fetchPromiseRef.current;

        const generation = generationRef.current;
        setLoading(true);
        const promise = api
            .get(userIdRef.current)
            .then((prefs) => {
                if (generation !== generationRef.current) return;
                let merged: UserPreferences = { ...DEFAULT_PREFERENCES, ...prefs };
                remotePatchesRef.current.forEach((patch) => {
                    merged = applyPreferencesPatch(merged, patch);
                });
                remotePatchesRef.current = [];
                setLocal(merged);
                loadedRef.current = true;
                setLoaded(true);
                const unsaved = unsavedChoicesRef.current;
                unsavedChoicesRef.current = {};
                if (Object.keys(unsaved).length > 0) {
                    commitRef.current?.({ kind: 'merge', value: unsaved }, true);
                }
            })
            // A failed load leaves loadedRef false, so nothing is saved over the
            // row we could not read, and the next fetch() tries again.
            .catch(() => {})
            .finally(() => {
                if (fetchPromiseRef.current === promise) {
                    fetchPromiseRef.current = null;
                    setLoading(false);
                }
            });
        fetchPromiseRef.current = promise;
        return promise;
    }, [setLocal]);

    // A different user signed in (or the user signed out) without the page
    // reloading. Everything held here belongs to the previous user: drop it,
    // cancel their pending save so it cannot land on the new user's row, and
    // load the new user's preferences.
    useEffect(() => {
        const previous = userIdRef.current;
        userIdRef.current = userId;
        if (previous === userId) return;
        if (previous !== null) {
            generationRef.current += 1;
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            fetchPromiseRef.current = null;
            remotePatchesRef.current = [];
            unsavedChoicesRef.current = {};
            loadedRef.current = false;
            setLoaded(false);
            setLoading(false);
            setLocal(DEFAULT_PREFERENCES);
        }
        if (userId !== null) fetch();
    }, [userId, fetch, setLocal]);

    // Another tab saved a change: take it, so this tab neither shows a stale
    // layout nor writes its stale copy back over the change.
    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') return undefined;
        const channel = new BroadcastChannel(CHANNEL_NAME);
        channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
            const { userId: senderId, patch } = event.data || ({} as ChannelMessage);
            if (!patch || !senderId || senderId !== userIdRef.current) return;
            if (!loadedRef.current) {
                remotePatchesRef.current.push(patch);
                return;
            }
            setLocal(applyPreferencesPatch(latestPrefsRef.current, patch));
        };
        channelRef.current = channel;
        return () => {
            channel.close();
            channelRef.current = null;
        };
    }, [setLocal]);

    const save = useCallback(() => {
        const forUserId = userIdRef.current;
        return api.update(latestPrefsRef.current, forUserId).catch(() => {});
    }, []);

    /**
     * Apply a change here, tell the other tabs, and save. Returns false, saving
     * nothing, while this user's preferences have not been loaded: the request
     * would carry defaults and replace every layout the user has saved.
     */
    const commit = useCallback(
        (patch: PreferencesPatch, immediate: boolean) => {
            if (!loadedRef.current) return false;
            setLocal(applyPreferencesPatch(latestPrefsRef.current, patch));
            if (userIdRef.current) {
                channelRef.current?.postMessage({ userId: userIdRef.current, patch } as ChannelMessage);
            }

            // One timer shared by every writer: a drag fires resize and move
            // events together, and each reads the ref the previous call already
            // updated, so the single deferred request carries all of them.
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (immediate) {
                debounceTimerRef.current = null;
                save();
            } else {
                debounceTimerRef.current = setTimeout(() => {
                    debounceTimerRef.current = null;
                    save();
                }, 500);
            }
            return true;
        },
        [setLocal, save]
    );
    commitRef.current = commit;

    const update = useCallback(
        async (partial: Partial<UserPreferences>) => {
            if (!loadedRef.current) await fetch();
            if (!commit({ kind: 'merge', value: partial }, true)) {
                // Still unreadable: show the choice now, save it once a load succeeds.
                unsavedChoicesRef.current = { ...unsavedChoicesRef.current, ...partial };
                setLocal({ ...latestPrefsRef.current, ...partial });
            }
        },
        [fetch, commit, setLocal]
    );

    const updateColumnState = useCallback(
        (pageKey: string, columnState: any[]) => {
            commit({ kind: 'columnState', pageKey, value: columnState }, false);
        },
        [commit]
    );

    const updateColumnWidths = useCallback(
        (pageKey: string, widths: Record<string, number>) => {
            commit({ kind: 'columnWidths', pageKey, value: widths }, false);
        },
        [commit]
    );

    const updateDashboardPref = useCallback(
        (key: string, value: unknown) => {
            commit({ kind: 'dashboard', key, value }, false);
        },
        [commit]
    );

    useDisplayZoom(preferences.displayZoom, loaded);
    useSidebarWidth(preferences.sidebarWidth, loaded);

    const contextValue = useMemo(
        () => ({
            preferences,
            loading,
            loaded,
            fetch,
            update,
            updateColumnState,
            updateColumnWidths,
            updateDashboardPref
        }),
        [preferences, loading, loaded, fetch, update, updateColumnState, updateColumnWidths, updateDashboardPref]
    );

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default UserPreferencesProvider;
